import type {
  BacktestComparisonResult,
  BacktestRole,
  ComparisonDailyResult,
  ComparisonEvent,
  ComparisonPathResult,
  ComparisonStrategy,
} from '@/api/mediumTermBacktest';

export type PortfolioKey = ComparisonStrategy | 'FLAT' | 'ORACLE';

export interface PortfolioOption {
  key: PortfolioKey;
  label: string;
  path: ComparisonPathResult;
}

export interface StrategySummaryView {
  strategy: ComparisonStrategy;
  label: string;
  openingLabel: string;
  openedMwh: number;
  weightedExecutionPrice: number | null;
  weightedSpotPrice: number | null;
  priceEdge: number | null;
  realizedPnl: number;
  terminalPnl: number;
  totalPnl: number;
}

export interface DailyContributionView {
  targetDate: string;
  value: number;
  cumulative: number;
}

export interface ComparisonExplanationView {
  headline: string;
  comparable: boolean;
  tie: boolean;
  leadAmount: number;
  leader: ComparisonStrategy | null;
  summaries: Record<ComparisonStrategy, StrategySummaryView>;
  contributions: DailyContributionView[];
  positiveContributors: DailyContributionView[];
  negativeContributors: DailyContributionView[];
}

const PORTFOLIO_LABELS: Record<string, string> = {
  BUYER_MOMENTUM: '动量',
  BUYER_REVERSAL: '反转',
  BUYER_FLAT_BENCHMARK: '被动平铺',
  BUYER_TERMINAL_SPOT_ORACLE: 'Oracle',
  SELLER_MOMENTUM: '动量',
  SELLER_REVERSAL: '反转',
  SELLER_FLAT_BENCHMARK: '被动平铺',
  SELLER_ZERO_BENCHMARK: '空仓基准',
  SELLER_TERMINAL_SPOT_ORACLE: 'Oracle',
};

export const ACTION_LABELS: Record<ComparisonEvent['action'], string> = {
  BUY_OPEN: '买入',
  SELL_REDUCE: '卖出减仓',
  SELL_OPEN: '卖出',
  BUY_COVER: '买回',
};

export const INTENT_LABELS: Record<ComparisonEvent['intent'], string> = {
  INITIAL_POSITION: '初始建仓',
  BENCHMARK: '基准建仓',
  SIGNAL: '信号交易',
  COMPLIANCE: '合规补仓',
  ORACLE: 'Oracle',
};

export const CONSTRAINT_LABELS: Record<string, string> = {
  NO_POSITION_TO_COVER: '无可买回仓位',
  TARGET_POSITION_LIMIT: '触及单日仓位上限',
  UPPER_BOUND_LIMIT: '触及买方月度上界',
  LOWER_BOUND_RECOVERABILITY_LIMIT: '受买方下界可恢复性限制',
  COMPLIANCE_CAPACITY_LIMIT: '可补仓容量不足',
};

export function formatNumber(value: number, digits = 2): string {
  return new Intl.NumberFormat('zh-CN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

export function formatQuantity(value: number): string {
  if (value > 0 && value < 0.01) return '<0.01';
  return formatNumber(value);
}

export function formatSigned(value: number, digits = 2): string {
  if (Math.abs(value) < 0.5 * (10 ** -digits)) return formatNumber(0, digits);
  return `${value > 0 ? '+' : ''}${formatNumber(value, digits)}`;
}

export function shortDate(value: string | null): string {
  return value ? value.slice(5) : '-';
}

export function portfolioLabel(portfolioType: string): string {
  return PORTFOLIO_LABELS[portfolioType] ?? portfolioType;
}

export function totalPnlFromDaily(path: ComparisonPathResult): number {
  return path.daily_results.reduce((sum, day) => sum + day.pnl, 0);
}

export function portfolioOptions(result: BacktestComparisonResult): PortfolioOption[] {
  const flatKey = result.role === 'buyer' ? 'BUYER_FLAT' : 'SELLER_FLAT';
  const flat = result.benchmarks[flatKey];
  const flatLabel = result.role === 'seller'
    ? `被动平铺 ${formatNumber(result.parameters.seller_flat_ratio * 100, 0)}%`
    : '被动平铺';
  return [
    { key: 'MOMENTUM', label: '动量', path: result.strategies.MOMENTUM },
    { key: 'REVERSAL', label: '反转', path: result.strategies.REVERSAL },
    ...(flat ? [{ key: 'FLAT' as const, label: flatLabel, path: flat }] : []),
    { key: 'ORACLE', label: 'Oracle', path: result.oracle },
  ];
}

function openingAction(role: BacktestRole): ComparisonEvent['action'] {
  return role === 'buyer' ? 'BUY_OPEN' : 'SELL_OPEN';
}

export function deriveStrategySummary(
  strategy: ComparisonStrategy,
  path: ComparisonPathResult,
  role: BacktestRole,
): StrategySummaryView {
  const openingEvents = path.events.filter(
    (event) => event.action === openingAction(role) && event.filled_quantity_mwh > 0,
  );
  const openedMwh = openingEvents.reduce((sum, event) => sum + event.filled_quantity_mwh, 0);
  const weightedExecutionPrice = openedMwh > 0
    ? openingEvents.reduce(
      (sum, event) => sum + event.filled_quantity_mwh * event.execution_price,
      0,
    ) / openedMwh
    : null;
  const weightedSpotPrice = openedMwh > 0
    ? openingEvents.reduce(
      (sum, event) => sum + event.filled_quantity_mwh * event.spot_price,
      0,
    ) / openedMwh
    : null;
  const priceEdge = weightedExecutionPrice == null || weightedSpotPrice == null
    ? null
    : role === 'buyer'
      ? weightedSpotPrice - weightedExecutionPrice
      : weightedExecutionPrice - weightedSpotPrice;

  return {
    strategy,
    label: strategy === 'MOMENTUM' ? '动量' : '反转',
    openingLabel: role === 'buyer' ? '实际买入' : '实际卖出',
    openedMwh,
    weightedExecutionPrice,
    weightedSpotPrice,
    priceEdge,
    realizedPnl: path.daily_results.reduce((sum, day) => sum + day.realized_pnl, 0),
    terminalPnl: path.daily_results.reduce((sum, day) => sum + day.terminal_pnl, 0),
    totalPnl: totalPnlFromDaily(path),
  };
}

export function deriveComparisonExplanation(
  result: BacktestComparisonResult,
): ComparisonExplanationView {
  const momentum = result.strategies.MOMENTUM;
  const reversal = result.strategies.REVERSAL;
  const momentumByDate = new Map(momentum.daily_results.map((day) => [day.target_date, day.pnl]));
  const reversalByDate = new Map(reversal.daily_results.map((day) => [day.target_date, day.pnl]));
  const dates = Array.from(new Set([...momentumByDate.keys(), ...reversalByDate.keys()])).sort();
  const momentumTotal = totalPnlFromDaily(momentum);
  const reversalTotal = totalPnlFromDaily(reversal);
  const reversalAdvantage = reversalTotal - momentumTotal;
  let cumulative = 0;
  const contributions = dates.map((targetDate, index) => {
    const value = (reversalByDate.get(targetDate) ?? 0) - (momentumByDate.get(targetDate) ?? 0);
    cumulative += value;
    if (index === dates.length - 1) cumulative = reversalAdvantage;
    return { targetDate, value, cumulative };
  });
  const tie = result.run_status === 'COMPLETED' && reversalAdvantage === 0;
  const hasWinner = result.run_status === 'COMPLETED'
    && !tie
    && result.comparison_leader !== null;
  const comparable = tie || hasWinner;
  const leader = hasWinner ? result.comparison_leader : null;
  const leadAmount = Math.abs(reversalAdvantage);
  const leaderLabel = leader === 'REVERSAL' ? '反转' : '动量';
  const otherLabel = leader === 'REVERSAL' ? '动量' : '反转';

  return {
    headline: tie
      ? `动量与反转持平 ${formatNumber(0)} 元`
      : hasWinner
        ? `${leaderLabel}比${otherLabel}多赚 ${formatNumber(leadAmount)} 元`
        : '本次回测不可比较',
    comparable,
    tie,
    leadAmount,
    leader,
    summaries: {
      MOMENTUM: deriveStrategySummary('MOMENTUM', momentum, result.role),
      REVERSAL: deriveStrategySummary('REVERSAL', reversal, result.role),
    },
    contributions,
    positiveContributors: contributions
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 3),
    negativeContributors: contributions
      .filter((item) => item.value < 0)
      .sort((a, b) => a.value - b.value)
      .slice(0, 3),
  };
}

function signalDirection(event: ComparisonEvent): string {
  if (event.signal_type === 'MOMENTUM') {
    return event.spread != null && event.spread > 0 ? '按上涨方向' : '按下跌方向';
  }
  return '按价格偏离回归方向';
}

export function eventReason(event: ComparisonEvent, role: BacktestRole): string {
  if (event.intent === 'ORACLE') {
    const edge = event.oracle_edge ?? event.spot_price - event.execution_price;
    return `Oracle 比较窗口价 ${formatNumber(event.execution_price)} 与标的日现货 ${formatNumber(event.spot_price)}，终值价差 ${formatSigned(edge)} 元。`;
  }
  if (event.intent === 'BENCHMARK') return '按月度基线价建立被动平铺仓位。';
  if (event.intent === 'INITIAL_POSITION') return '按已确认的买方月度持仓下界建立初始仓位。';
  if (event.intent === 'COMPLIANCE') return '为恢复买方月度持仓下界，在远端可交易标的上补仓。';
  if (event.spread == null) return '按本窗口记录的交易信号执行。';

  const relation = event.spread >= 0 ? '高于' : '低于';
  const roleLabel = role === 'buyer' ? '买方' : '卖方';
  const strategyLabel = event.signal_type === 'MOMENTUM' ? '动量' : '反转';
  return `窗口价 ${formatNumber(event.execution_price)}，${relation}月度基线 ${formatNumber(event.baseline_price)} 共 ${formatNumber(Math.abs(event.spread))} 元；${roleLabel}${strategyLabel}${signalDirection(event)}${ACTION_LABELS[event.action]} ${formatQuantity(event.filled_quantity_mwh)} MWh。`;
}

export function dailyResult(path: ComparisonPathResult, targetDate: string): ComparisonDailyResult | null {
  return path.daily_results.find((day) => day.target_date === targetDate) ?? null;
}

export function constraintText(event: ComparisonEvent): string {
  return event.constraint_reasons.map((reason) => CONSTRAINT_LABELS[reason] ?? reason).join('、') || '-';
}
